import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('commission_rate');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/affiliate-programs`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    'all',
    'electronics',
    'books',
    'home',
    'fashion',
    'digital_products',
    'software',
    'courses',
    'ebooks',
    'beauty',
    'technology',
    'retail',
    'travel',
    'finance'
  ];

  const filteredPrograms = programs.filter(program => {
    if (selectedCategory === 'all') return true;
    return program.categories.includes(selectedCategory);
  });

  const sortedPrograms = [...filteredPrograms].sort((a, b) => {
    switch (sortBy) {
      case 'commission_rate':
        return b.commission_rate - a.commission_rate;
      case 'cookie_duration':
        return b.cookie_duration - a.cookie_duration;
      case 'minimum_payout':
        return a.minimum_payout - b.minimum_payout;
      default:
        return 0;
    }
  });

  const getNetworkColor = (network) => {
    const colors = {
      amazon: 'bg-orange-100 text-orange-800',
      clickbank: 'bg-blue-100 text-blue-800',
      shareasale: 'bg-green-100 text-green-800',
      cj_affiliate: 'bg-purple-100 text-purple-800'
    };
    return colors[network] || 'bg-gray-100 text-gray-800';
  };

  const getCommissionColor = (rate) => {
    if (rate >= 50) return 'text-green-600 font-bold';
    if (rate >= 20) return 'text-blue-600 font-semibold';
    if (rate >= 10) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Affiliate Programs</h1>
        <p className="text-gray-600">Discover and compare the best affiliate programs for your niche</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="commission_rate">Commission Rate (High to Low)</option>
              <option value="cookie_duration">Cookie Duration (High to Low)</option>
              <option value="minimum_payout">Minimum Payout (Low to High)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sortedPrograms.map(program => (
          <div key={program.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{program.name}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNetworkColor(program.network)}`}>
                  {program.network.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${getCommissionColor(program.commission_rate)}`}>
                  {program.commission_rate}%
                </div>
                <div className="text-sm text-gray-600">Commission</div>
              </div>
            </div>

            <p className="text-gray-600 mb-4">{program.description}</p>

            {/* Program Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Cookie Duration</div>
                <div className="font-semibold text-lg">{program.cookie_duration} days</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Min. Payout</div>
                <div className="font-semibold text-lg">${program.minimum_payout}</div>
              </div>
            </div>

            {/* Payment Frequency */}
            <div className="mb-4">
              <div className="text-sm text-gray-600">Payment Frequency</div>
              <div className="font-medium capitalize">{program.payment_frequency}</div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <div className="text-sm text-gray-600 mb-2">Categories</div>
              <div className="flex flex-wrap gap-2">
                {program.categories.slice(0, 4).map(category => (
                  <span
                    key={category}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {category.replace('_', ' ')}
                  </span>
                ))}
                {program.categories.length > 4 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    +{program.categories.length - 4} more
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <a
                href={program.signup_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-lg font-medium"
              >
                Join Program
              </a>
              <button
                onClick={() => window.open(`/links?program_id=${program.id}`, '_blank')}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-center py-2 px-4 rounded-lg font-medium"
              >
                Create Link
              </button>
            </div>

            {/* Quick Stats */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between text-sm text-gray-600">
                <span>🎯 High Converting</span>
                <span>⚡ Quick Approval</span>
                <span>💰 Reliable Payments</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedPrograms.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No programs found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your filters to see more programs</p>
          <button
            onClick={() => {
              setSelectedCategory('all');
              setSortBy('commission_rate');
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg"
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* Program Comparison */}
      <div className="mt-12 bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Program Comparison</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cookie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Min. Payout
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {programs.map(program => (
                <tr key={program.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{program.name}</div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getNetworkColor(program.network)}`}>
                      {program.network.replace('_', ' ')}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap font-semibold ${getCommissionColor(program.commission_rate)}`}>
                    {program.commission_rate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {program.cookie_duration} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${program.minimum_payout}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {program.payment_frequency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProgramsPage;